import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/context/UserContext";
import { money, orderAmounts, renderOrder, validateOrder, type WineOrder } from "./document";
import { generateRandomOrder } from "./randomOrder";
import type { ProductCatalogItem } from "./randomOrder";
import { supabase } from "@/lib/supabase";

function today() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function initialOrder(name: string): WineOrder {
  const date = today();
  return {
    date, number: `WINE-${date.replaceAll("-", "")}-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
    author: name || "", customer: "", contact: "", salesperson: name || "", sku: "WINE-001",
    status: "已預定", product: "Silver Oak Cabernet Sauvignon", origin: "美國・Alexander Valley", vintage: "2018",
    volume: "750 ml", alcohol: "13.5%", quantity: "12", price: "5000", deposit: "10000", payment: "銀行轉帳",
    description: "",
    notes: "請核對酒款、年份、瓶數及付款金額。交貨時間依雙方確認為準。",
  };
}

const inputClass = "w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400";

export default function WineOrderGenerator() {
  const { userName } = useUser();
  const [form, setForm] = useState<WineOrder>(() => initialOrder(userName));
  const [generated, setGenerated] = useState<WineOrder | null>(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const frame = useRef<HTMLIFrameElement>(null);
  const html = useMemo(() => generated ? renderOrder(generated) : "", [generated]);
  const amounts = orderAmounts(form);
  const changed = generated && JSON.stringify(form) !== JSON.stringify(generated);

  useEffect(() => {
    supabase.from("products").select("brand,name,vintage,country,region,grape,selling_price,description,category")
      .eq("status", "active").then(({ data }) => setCatalog((data || []) as ProductCatalogItem[]));
  }, []);

  const update = (key: keyof WineOrder, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };
  const field = (key: keyof WineOrder, label: string, options?: { type?: string; required?: boolean; min?: number; max?: number; step?: number; placeholder?: string }) => (
    <label className="flex flex-col gap-1.5 text-xs text-foreground-600" key={key}>
      <span>{label}{options?.required && <span className="text-accent-600"> *</span>}</span>
      <input className={inputClass} value={form[key]} onChange={(e) => update(key, e.target.value)}
        type={options?.type || "text"} required={options?.required} min={options?.min} max={options?.max}
        step={options?.step} placeholder={options?.placeholder} maxLength={options?.type === "number" ? undefined : 200} />
    </label>
  );
  const select = (key: keyof WineOrder, label: string, choices: string[]) => (
    <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
      {label}<select className={inputClass} value={form[key]} onChange={(e) => update(key, e.target.value)}>
        {choices.map((choice) => <option key={choice}>{choice}</option>)}
      </select>
    </label>
  );
  const download = () => {
    if (!generated) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${generated.number.replace(/[^\w\u4e00-\u9fff-]/g, "_")}.html`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1 text-xs font-semibold tracking-widest text-primary-600">ORDER DOCUMENT</p>
          <h1 className="text-xl font-bold text-foreground-900">酒類訂單產生器</h1>
          <p className="mt-1 text-sm text-foreground-500">填寫訂單與付款資料，產生可列印的尾款支付清單。</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-primary-50 px-3 py-1.5 text-xs text-primary-700">A4 單據・新臺幣計價</span>
          <button type="button" onClick={() => {
            const next = generateRandomOrder(form, userName, catalog);
            setForm(next);
            setGenerated(next);
            setReady(false);
            setError("");
          }} className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600">
            <i className="ri-shuffle-line" aria-hidden="true" />一鍵隨機生成
          </button>
        </div>
      </div>

      <p className="text-xs text-foreground-500">一鍵帶入虛構客戶、示範酒款與付款資料，產生後仍可自由修改。</p>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.3fr)]">
        <form className="space-y-4" onSubmit={(event) => {
          event.preventDefault();
          const problem = validateOrder(form);
          if (problem) { setError(problem); return; }
          setReady(false);
          setGenerated({ ...form });
        }}>
          <section className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 text-sm font-semibold text-foreground-900">01 ／ 清單與客戶資訊</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field("date", "清單日期", { type: "date", required: true })}
              {field("number", "清單編號", { required: true })}
              {field("author", "填寫人", { required: true })}
              {select("status", "訂單狀態", ["已預定", "待付款", "備貨中", "已出貨", "已完成"])}
              {field("customer", "客戶名稱", { required: true, placeholder: "請輸入客戶姓名或公司名稱" })}
              {field("contact", "聯絡方式", { required: true, placeholder: "電話或電子信箱" })}
              {field("salesperson", "專屬業務人員", { required: true })}
              {field("sku", "商品編號", { required: true })}
            </div>
          </section>
          <section className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 text-sm font-semibold text-foreground-900">02 ／ 酒款資訊</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field("product", "酒款名稱", { required: true })}
              {field("origin", "產地")}
              {field("vintage", "年份", { placeholder: "例如：2020 或 NV" })}
              {field("volume", "每瓶容量", { placeholder: "例如：750 ml" })}
              {field("alcohol", "酒精濃度", { placeholder: "例如：13.5%" })}
              {field("quantity", "訂購瓶數", { type: "number", min: 1, max: 100000, step: 1, required: true })}
            </div>
            <label className="mt-4 flex flex-col gap-1.5 text-xs text-foreground-600">酒款說明
              <textarea className={inputClass} rows={3} maxLength={1200} value={form.description} onChange={(e) => update("description", e.target.value)} />
            </label>
          </section>
          <section className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 text-sm font-semibold text-foreground-900">03 ／ 付款資料</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field("price", "每瓶單價（NT$）", { type: "number", min: 1, max: 100000000, step: 1, required: true })}
              {field("deposit", "已付訂金（NT$）", { type: "number", min: 0, step: 1, required: true })}
              {select("payment", "支付方式", ["銀行轉帳", "現金", "信用卡", "其他"])}
            </div>
            <div className="mt-4 rounded-lg bg-primary-50 p-4 text-sm">
              <div className="flex justify-between text-foreground-600"><span>商品總價</span><span>{Number.isFinite(amounts.total) ? money(amounts.total) : "—"}</span></div>
              <div className="mt-2 flex justify-between font-semibold text-primary-700"><span>應付尾款</span><span>{Number.isFinite(amounts.balance) ? money(amounts.balance) : "—"}</span></div>
            </div>
            <label className="mt-4 flex flex-col gap-1.5 text-xs text-foreground-600">備註
              <textarea className={inputClass} rows={3} maxLength={1500} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
            </label>
          </section>
          {error && <p role="alert" className="rounded-lg bg-accent-50 p-3 text-sm text-accent-700">{error}</p>}
          <div className="flex flex-wrap gap-3">
            <button type="submit" className="flex-1 rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-600"><i className="ri-file-add-line mr-2" />{generated ? "更新清單" : "產生清單"}</button>
            <button type="button" className="rounded-lg border border-background-200 px-4 py-3 text-sm text-foreground-600 hover:bg-background-100" onClick={() => {
              if (!window.confirm("確定重設填寫內容並建立新單號？")) return;
              setForm(initialOrder(userName)); setGenerated(null); setError(""); setReady(false);
            }}>重設</button>
          </div>
          <p className="text-xs text-foreground-400">此頁用於製作付款清單，產生單據不會新增貨運或帳務紀錄。</p>
        </form>

        <section className="min-w-0 overflow-hidden rounded-xl border border-background-200 bg-background-100/60 xl:sticky xl:top-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 bg-background-50 p-4">
            <div><h2 className="text-sm font-semibold text-foreground-900">單據預覽</h2><p className="mt-0.5 text-xs text-foreground-400">{generated ? generated.number : "填寫完成後，點選「產生清單」"}</p></div>
            <div className="flex gap-2">
              <button type="button" disabled={!generated || !ready || !!changed} onClick={download} className="rounded-lg border border-background-200 px-3 py-2 text-xs text-foreground-700 disabled:opacity-40">下載 HTML</button>
              <button type="button" disabled={!generated || !ready || !!changed} onClick={() => { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print(); }} className="rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"><i className="ri-printer-line mr-1" />列印／另存 PDF</button>
            </div>
          </div>
          {changed && <p role="status" className="bg-accent-50 px-4 py-3 text-xs text-accent-700">內容已修改，請點選「更新清單」後再列印或下載。</p>}
          {generated ? (
            <div className="overflow-x-auto p-3 md:p-5"><iframe ref={frame} title="酒類訂單尾款支付清單" srcDoc={html} sandbox="allow-same-origin allow-modals" onLoad={() => setReady(true)} className="mx-auto h-[1060px] w-[794px] max-w-none border-0 bg-white shadow-sm" /></div>
          ) : (
            <div className="flex min-h-[540px] flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-primary-600"><i className="ri-file-list-3-line text-3xl" /></div>
              <h3 className="text-base font-semibold text-foreground-800">一張清楚的酒類付款清單</h3>
              <p className="mt-2 max-w-xs text-sm leading-6 text-foreground-500">客戶資訊、酒款規格與尾款金額，依照 A4 格式整理，方便確認與交付。</p>
              <div className="mt-6 flex gap-2 text-xs text-foreground-400"><span>客戶資訊</span><span>／</span><span>酒款明細</span><span>／</span><span>付款金額</span></div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
